import {
  BORROWER_INFO,
  CURRENT_POLICY,
  DRIVER_LICENCE,
  dateOfBirth,
  effectiveDate,
  firstName,
  lastName,
  mortgagee,
  phones,
  prefilled,
  premiumPayer,
  priorCoverages,
  priorPremium,
  propertyAddress,
} from "../industry.ts";
import type { Requirement } from "../requirements.ts";
import type { Carrier } from "./carrier.ts";

/** National General's Custom360 Home (homeowners), as the recorded quote of 2026-10-05 asked it. */
const REQUIREMENTS: Requirement[] = [
  firstName,
  lastName,
  dateOfBirth,
  {
    key: "gender",
    label: "Gender",
    from: "client",
    need: "provide",
    ask: "The insured's gender (female or male)",
    lookIn:
      "the borrower's driver's licence or state ID in the loan's documents; never the 1003's demographics",
  },
  {
    key: "maritalStatus",
    label: "Marital status",
    from: "client",
    need: "provide",
    assume: {
      value: "single",
      basis: "assumed; a loan, when read, shows a spouse when there is one",
    },
    ask: "The insured's marital status (single, married, divorced, separated, widowed)",
    lookIn: BORROWER_INFO,
  },
  {
    key: "occupation",
    label: "Occupation",
    from: "answer",
    need: "provide",
    assume: { value: "none of these", basis: "assumed; few clients are in these" },
    ask: "Is the insured an athlete or entertainer, in a high-profile profession, a journalist, in the military, a politician, a student, or none of these?",
  },
  phones,
  {
    key: "serviceConsent",
    label: "Consent to service calls and texts",
    from: "answer",
    need: "provide",
    ask: "Does the insured consent to policy and driver service calls and texts (yes or no)?",
  },
  propertyAddress,
  {
    key: "occupancy",
    label: "Occupancy",
    from: "property",
    need: "provide",
    assume: { value: "primary, owner-occupied", basis: "assumed for a homeowners quote" },
    verifyWith: "producer",
    ask: "How the home is used: primary, secondary (seasonal or not) or vacant, and owner- or tenant-occupied",
    lookIn: `${BORROWER_INFO}: the 1003's occupancy`,
  },
  {
    key: "namedInsuredType",
    label: "Named insured is",
    from: "answer",
    need: "provide",
    assume: {
      value: "owner",
      basis: "assumed: the client owns the home (the borrower, when a loan is read)",
    },
    ask: "Is the insured the owner, a tenant or a life tenant of the home?",
  },
  {
    key: "datePurchased",
    label: "Date purchased",
    from: "property",
    need: "provide",
    verifyWith: "producer",
    ask: "When the insured bought the home",
    lookIn:
      "the loan's documents: the title commitment's vesting deed (read all its pages), a purchase loan's Closing Disclosure (its closing date), or the appraisal's prior sale; then the last sale on Zillow's page for the address (the property lookup); failing those, the time at the current address (owned) in Borrower Info, as an approximate date",
  },
  {
    key: "hailResistantRoof",
    label: "Hail-resistant roof",
    from: "answer",
    need: "provide",
    assume: { value: "no", basis: "assumed; the cautious answer, which may price higher" },
    ask: "Is the roof hail (impact) resistant (yes or no)?",
  },
  {
    key: "roofYear",
    label: "Roof replaced",
    from: "property",
    need: "confirm",
    assume: {
      derive: "yearsAgo:15",
      notBefore: "yearBuilt",
      basis: "no record of a replacement: assumed 15 years old, never older than the house",
    },
    ask: "The year the roof was last fully replaced, if known",
    lookIn:
      "the seller's disclosure or the appraisal in the loan's documents, then a reroof or roofing permit on the county's property card (the property lookup)",
  },
  ...prefilled("yearBuilt", "construction", "roofType", "squareFeet", "stories"),
  {
    key: "vehiclesGaraged",
    label: "Vehicles garaged at the home",
    from: "answer",
    need: "provide",
    ask: "How many vehicles are garaged at the home?",
  },
  {
    key: "priorCarrier",
    label: "Prior carrier",
    from: "property",
    need: "provide",
    assume: { value: "Other insurance companies", basis: "no current policy on record" },
    verifyWith: "producer",
    ask: "Who insures the home now, or 'none' if it has no insurance",
    lookIn: CURRENT_POLICY,
  },
  {
    key: "priorPolicyExpiration",
    label: "Prior policy expires",
    from: "property",
    need: "provide",
    when: { key: "priorCarrier", notEquals: "none" },
    future: true,
    assume: {
      derive: "sameAs:effectiveDate",
      basis: "no current policy on record; the new one starts when it would end",
    },
    verifyWith: "producer",
    ask: "When the current policy expires",
    lookIn: CURRENT_POLICY,
  },
  {
    key: "yearsInsured",
    label: "Years continuously insured",
    from: "property",
    need: "provide",
    when: { key: "priorCarrier", notEquals: "none" },
    assume: {
      derive: "yearsSince:datePurchased",
      basis: "insured without a gap since the purchase",
    },
    ask: "How many years the home has been insured without a gap",
    lookIn: `${CURRENT_POLICY}; otherwise it follows from the purchase date`,
  },
  priorPremium,
  {
    ...effectiveDate,
    assume: { derive: "tomorrow", basis: "the earliest start; no current policy on record" },
  },
  {
    ...premiumPayer,
    assume: { value: "insured", basis: "no mortgage on record: the insured pays in full" },
  },
  mortgagee,
  {
    key: "deductible",
    label: "All perils deductible",
    from: "quote",
    need: "provide",
    verifyWith: "choice",
    ask: "The all perils deductible: $500, $1,000, $1,500, $2,000, $2,500, $5,000, $7,500, $10,000, or 0.5%, 1%, 2%, 3% or 5%",
    lookIn: CURRENT_POLICY,
  },
  {
    key: "windHailDeductible",
    label: "Wind/hail deductible",
    from: "answer",
    need: "provide",
    verifyWith: "choice",
    ask: "The wind/hail deductible: 1%, 2%, 5% or a dollar amount",
    lookIn: CURRENT_POLICY,
  },
  {
    key: "dwellingCoverage",
    label: "Dwelling (Coverage A)",
    from: "quote",
    need: "confirm",
    mirror: true,
    verifyWith: "choice",
    ask: "A dwelling limit, if not the carrier's replacement cost",
    lookIn: CURRENT_POLICY,
  },
  {
    key: "liabilityLimit",
    label: "Liability",
    from: "quote",
    need: "confirm",
    mirror: true,
    verifyWith: "choice",
    ask: "A liability limit, if not the carrier's $100,000",
    lookIn: CURRENT_POLICY,
  },
  priorCoverages,
  {
    key: "bundle",
    label: "Bundle with other products",
    from: "answer",
    need: "provide",
    verifyWith: "choice",
    ask: "Should this be bundled with another National General product, such as auto (yes or no)?",
  },
  {
    key: "eligibility",
    label: "Eligibility questions",
    from: "answer",
    need: "provide",
    assume: { value: "no to all", basis: "assumed; the client has not been asked" },
    ask: "Yes or no for each: in the past 5 years a foreclosure, bankruptcy, judgment or lien; exotic or vicious animals; home on pilings, piers or an open foundation; knob-and-tube or aluminum wiring or existing damage; home for sale; on a historic register; short-term rentals (Airbnb, Vrbo); a skateboard or bicycle ramp or a treehouse over 12 feet; commercial or farming exposure; accidents or claims in the past 30 days",
  },
  {
    key: "driverLicence",
    label: "Driver's licence",
    from: "answer",
    need: "in-portal",
    ask: "The insured's driver's licence status, state and number, which National General asks for at its last step",
    lookIn: `${DRIVER_LICENCE}; typed into the portal, never saved`,
  },
];

/**
 * National General as a carrier (the carrier layer): its portal, its questions and its own
 * defaults for a preliminary quote. An agency's defaults are not here (`agency/`).
 */
export const NATIONAL_GENERAL: Carrier = {
  name: "National General",
  aliases: ["NatGen", "NGIC", "National General Insurance"],
  portalUrl: "https://natgenagency.com/Login.aspx?Menu=Login",
  lines: "homeowners (Custom360 Home)",
  requirements: REQUIREMENTS,
};
